import { db } from '../config/firebase';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { collection, getDocs, query, where, orderBy, doc, updateDoc } from 'firebase/firestore';

/**
 * AI Service
 * Handles AI-powered feedback for student submissions
 * Now uses secure Firebase Functions to protect API keys
 */

const functions = getFunctions();

// Connect to Functions emulator if explicitly enabled
// Only connect if REACT_APP_USE_EMULATOR is explicitly set to 'true'
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';

if (useEmulator) {
  console.log('🔧 Using Firebase Functions Emulator (localhost:5002)');
  connectFunctionsEmulator(functions, 'localhost', 5002);
}

export const aiService = {
  /**
   * Check if AI service is configured
   * @returns {boolean} Always returns true (checked server-side)
   */
  isConfigured() {
    return true; // Server-side will handle configuration check
  },

  /**
   * Generate AI feedback for a file submission
   * Calls secure Firebase Function instead of exposing API key
   * 
   * @param {Object} params - Parameters for feedback generation
   * @param {string} params.prompt - Custom prompt from widget configuration
   * @param {string} params.fileName - Name of the uploaded file
   * @param {string} params.fileType - Type of file (extension)
   * @param {string} params.fileUrl - URL to the uploaded file
   * @param {string} params.userId - User ID who uploaded the file
   * @param {string} params.widgetId - Widget ID this submission is for
   * @param {string} params.moduleId - Module ID (optional)
   * @param {string} params.courseId - Course ID (optional)
   * @returns {Object} { feedback, feedbackId, error }
   */
  async generateFeedback({
    prompt,
    fileName,
    fileType,
    fileUrl,
    userId,
    widgetId,
    moduleId = null,
    courseId = null,
  }) {
    try {
      console.log('Calling Firebase Function: generateAIFeedback');
      
      // Call secure Firebase Function
      const generateAIFeedback = httpsCallable(functions, 'generateAIFeedback');
      const result = await generateAIFeedback({
        prompt,
        fileName,
        fileType,
        fileUrl,
        userId,
        widgetId,
        moduleId,
        courseId,
      });

      console.log('Firebase Function response:', result.data);

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to generate feedback');
      }

      return {
        feedback: result.data.feedback,
        feedbackId: result.data.feedbackId,
        error: null,
      };
    } catch (error) {
      console.error('Error generating AI feedback:', error);
      
      // Parse Firebase Functions error
      let errorMessage = 'Failed to generate feedback';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code) {
        errorMessage = `Error: ${error.code}`;
      }

      return {
        feedback: null,
        feedbackId: null,
        error: errorMessage,
      };
    }
  },


  /**
   * Get all feedback for a user
   * @param {string} userId - User ID
   * @returns {Object} { feedback: Array, error }
   */
  async getUserFeedback(userId) {
    try {
      const feedbackRef = collection(db, 'ai_feedback');
      const q = query(
        feedbackRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const feedback = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        generatedAt: doc.data().generatedAt?.toDate(),
      }));

      return { feedback, error: null };
    } catch (error) {
      console.error('Error getting user feedback:', error);
      return { feedback: [], error: error.message };
    }
  },

  /**
   * Get feedback for a specific submission
   * @param {string} userId - User ID
   * @param {string} widgetId - Widget ID
   * @param {string} fileName - File name
   * @returns {Object} { feedback, error }
   */
  async getSubmissionFeedback(userId, widgetId, fileName) {
    try {
      const feedbackRef = collection(db, 'ai_feedback');
      const q = query(
        feedbackRef,
        where('userId', '==', userId),
        where('widgetId', '==', widgetId),
        where('fileName', '==', fileName),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return { feedback: null, error: null };
      }

      const feedbackDoc = querySnapshot.docs[0];
      return {
        feedback: {
          id: feedbackDoc.id,
          ...feedbackDoc.data(),
          createdAt: feedbackDoc.data().createdAt?.toDate(),
          generatedAt: feedbackDoc.data().generatedAt?.toDate(),
        },
        error: null,
      };
    } catch (error) {
      console.error('Error getting submission feedback:', error);
      return { feedback: null, error: error.message };
    }
  },

  /**
   * Get feedback for a specific module
   * @param {string} userId - User ID
   * @param {string} moduleId - Module ID
   * @returns {Object} { feedback: Array, error }
   */
  async getModuleFeedback(userId, moduleId) {
    try {
      const feedbackRef = collection(db, 'ai_feedback');
      const q = query(
        feedbackRef,
        where('userId', '==', userId),
        where('moduleId', '==', moduleId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const feedback = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        generatedAt: doc.data().generatedAt?.toDate(),
      }));

      return { feedback, error: null };
    } catch (error) {
      console.error('Error getting module feedback:', error);
      return { feedback: [], error: error.message };
    }
  },

  /**
   * Mark feedback as read
   * @param {string} feedbackId - Feedback document ID
   * @returns {Object} { success, error }
   */
  async markFeedbackAsRead(feedbackId) {
    try {
      const feedbackRef = doc(db, 'ai_feedback', feedbackId);
      await updateDoc(feedbackRef, {
        read: true,
        readAt: new Date(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error marking feedback as read:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Test the API key configuration (Admin only)
   * Calls secure Firebase Function to test server-side API key
   * 
   * @returns {Object} { success, error }
   */
  async testApiKey() {
    try {
      console.log('Testing OpenAI API key via Firebase Function');
      
      // Call secure Firebase Function (admin-only)
      const testOpenAIKey = httpsCallable(functions, 'testOpenAIKey');
      const result = await testOpenAIKey();

      console.log('API key test result:', result.data);

      return {
        success: result.data.success,
        error: result.data.error,
      };
    } catch (error) {
      console.error('Error testing API key:', error);
      
      let errorMessage = 'Failed to test API key';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code === 'permission-denied') {
        errorMessage = 'Admin access required to test API key';
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  },
};

